import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useDispatch } from 'react-redux';

import {
  BaseWorkTaskTypeEnum,
  ContactInfo,
  CustomerAppointmentDTO,
  EmailNotificationTypeEnum,
  WorkInvoiceDTO,
  WorkOrderInfoDto,
  WorkTaskStatus
} from '../../../../api/api';
import { CameraIcon } from '../../../../assets/icons/CameraIcon';
import { ScheduleIcon } from '../../../../assets/icons/ScheduleIcon';
import TabsVertical from '../../../../blocks/tabs-vertical/TabsVertical';
import TabsVerticalToolsItem from '../../../../blocks/tabs-vertical/tabs-vertical-tools/tabs-vertical-tools-item/TabsVerticalToolsItem';
import StatusTag from '../../../../components/status-tag/StatusTag';
import useQuery from '../../../../hooks/useQuery';
import { QueryParams } from '../../../../models/QueryParams';
import { WorkTaskDTOExtended } from '../../../../models/TaskType';
import AttachmentService from '../../../../services/AttachmentService';
import NotificationService from '../../../../services/NotificationService';
import TaskService from '../../../../services/TaskService';
import { setListShouldUpdate } from '../../../../stateManagement/reducers/taskListReducer';
import { getEnumDisplayValue } from '../../../../utils/enumUtils';
import { log } from '../../../../utils/logging/log';
import TaskGoodsStep from '../goods-step/TaskGoodsStep';
import TaskFilesStep from '../task-files-step/TaskFilesStep';
import TaskMessagesStep from '../task-messages-step/TaskMessagesStep';
import TaskTimeRegStep from '../timereg-step/TaskTimeRegStep';
import WorkTaskControlBar from '../../../../blocks/worktask-control-bar/WorkTaskControlBar';
import { ConstructionIcon } from '../../../../assets/icons/ContructionIcon';
import { DialogBody } from '../../../../stateManagement/reducers/confirmDialogReducer';
import { useConfirmationDialog } from '../../../../hooks/useConfirmationDialog';
import useUserAccess from '../../../../hooks/useUserAccess';
import { isInPlanningLoop } from '../../task-list-view/TaskListFilterUtils';
import Typography from '../../../../components/typography/Typography';
import ToolTip from '../../../../components/tool-tip/ToolTip';
import { InfoIcon } from '../../../../assets/icons/InfoIcon';
import { EditIcon } from '../../../../assets/icons/EditIcon';
import MeterTaskChecklistStep from '../checklist-step/MeterTaskChecklistStep';
import WorkInvoiceStep from '../work-invoice-step/WorkInvoiceStep';
import { WarningIcon } from '../../../../assets/icons/WarningIcon';
import { UnreadDot } from '../../../../assets/icons/UnreadDot';
import theme from '../../../../theme/light-theme';
import WorkInvoiceService from '../../../../services/WorkInvoiceService';
import MeterTaskDetailsStep from '../details-step/MeterTaskDetailsStep';
import { AddDocumentIcon } from '../../../../assets/icons/AddDocumentIcon';
import MeterService from '../../../../services/MeterService';
import { AddIcon } from '../../../../assets/icons/AddIcon';
import SubWorkTaskService from '../../../../services/SubWorkTaskService';

export interface CollapsedSections {
  checklistId: number;
  taskTypeChecklistId: number;
  sections: number[];
}

export interface ExpandedCollapedChecklistsSections {
  expandedChecklistId?: number;
  expandedTaskTypeChecklistId?: number;
  collapsedSections?: CollapsedSections[];
}

enum InvoiceError {
  NONE = 'none',
  ALERT = 'alert',
  NOTIFICATION = 'notification'
}

interface Props {
  hideCloseButton?: boolean;
  toolsOpen: boolean;
  setToolsOpen: (open: boolean) => void;
  handleCloseTask: () => void;
  defaultActivePanelId: string;
  fetchTask: () => void;
  setTask: (task: WorkTaskDTOExtended | undefined) => void;
  task: WorkTaskDTOExtended | undefined;
  workInvoiceId?: number;
  workInvoice?: WorkInvoiceDTO;
  setWorkInvoiceId: (workInvoiceId?: number) => void;
  showWorkInvoiceError: InvoiceError;
  setWorkInvoice: (invoice?: WorkInvoiceDTO) => void;
  fetchWorkInvoiceNotification: () => void;
  fetchTaskCallback: () => void;
}

const MeterTaskDetailsViewContent = (props: Props) => {
  const {
    hideCloseButton,
    toolsOpen,
    setToolsOpen,
    handleCloseTask,
    defaultActivePanelId,
    fetchTask,
    setTask,
    task,
    workInvoice,
    workInvoiceId,
    fetchWorkInvoiceNotification,
    setWorkInvoice,
    setWorkInvoiceId,
    showWorkInvoiceError,
    fetchTaskCallback
  } = props;
  const { id, type: detailType } = useQuery<QueryParams>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [editable, setEditable] = useState(false);
  const [activePanelId, setActivePanelId] = useState<string>(defaultActivePanelId);
  const [customerAppointment, setCustomerAppointment] = useState<CustomerAppointmentDTO | undefined>();
  const [numberOfFiles, setNumberOfFiles] = useState<number>();
  const [showWorkTaskControlBar, setShowWorkTaskControlBar] = useState(false);
  const [meterDetails, setMeterDetails] = useState<WorkOrderInfoDto>();
  const [comment, setComment] = useState('');

  const [enableEmailNotification, setEnableEmailNotification] = useState(false);
  const [isEmailNotificationLoading, setIsEmailNotificationLoading] = useState(false);

  const { officeUserGroupAllowed } = useUserAccess();

  const dispatch = useDispatch();
  const [scrollPositions, setScrollPositions] = useState<Map<string, number>>(
    new Map([
      ['1', 0],
      ['02', 0],
      ['2', 0],
      ['3', 0],
      ['4', 0],
      ['5', 0],
      ['6', 0],
      ['7', 0],
      ['8', 0],
      ['9', 0]
    ])
  );
  const { getConfirmation } = useConfirmationDialog();

  const getNumberOfFiles = useCallback(() => {
    task?.id &&
      AttachmentService.getNumberOfFiles(parseInt(task?.id))
        .then((res) => setNumberOfFiles(res))
        .catch((err) => log(err));
  }, [task?.id]);

  useEffect(() => {
    getNumberOfFiles();
  }, [getNumberOfFiles]);

  useEffect(() => {
    if (!task?.id) return;
    MeterService.getMeterTaskInfo(parseInt(task?.id))
      .then((_meterDetails) => {
        setMeterDetails(_meterDetails.workOrderInfo);
      })
      .catch((error) => {
        NotificationService.error('Kunne ikke hente data fra WTM', 5000);
        log(error);
      });
  }, [task?.id]);

  const tabClickedHandler = useCallback(
    (newTabId: string) => {
      setScrollPositions((_scrollPositions) =>
        _scrollPositions.set(activePanelId, document.getElementById('task-details-body')?.scrollTop ?? 0)
      );
      setActivePanelId(newTabId);
    },
    [activePanelId]
  );

  const scrollIntoPreviousPositionHandler = useCallback(
    (id: string) => {
      document.getElementById('task-details-body')?.scrollBy({ top: scrollPositions.get(id) });
    },
    [scrollPositions]
  );

  const handleNumberOfFilesChanged = useCallback(() => {
    getNumberOfFiles();
  }, [getNumberOfFiles]);

  const handleCreateSubTask = useCallback(
    async (taskTypeId?: number) => {
      if (!id || !taskTypeId || !task) return;

      let newTask = task;
      newTask.subWorkTasks = (await TaskService.getTask(parseInt(id))).subWorkTasks;
      setTask(newTask);

      setIsLoading(true);
      SubWorkTaskService.createSubTask(parseInt(id), taskTypeId)
        .then((response) => {
          task?.subWorkTasks?.push(response);
          NotificationService.success(`Tilføjet ekstra tekniker tilhørende opgave ${id}`, 5000);
          dispatch(setListShouldUpdate(true));
        })
        .catch((err) => {
          log(err);
          NotificationService.error(`Kunne ikke tilføje ekstra tekniker til opgaven`, 5000);
        })
        .finally(() => setIsLoading(false));
    },
    [id, task, dispatch]
  );

  const renderTabsHeader = () => {
    if (task) {
      return (
        <StyledTabHeader>
          <Typography variant="h4" fontWeight="bold">
            {task.taskType?.description && (
              <ToolTip title={task.taskType?.description ?? ''}>
                <IconContainer>
                  <InfoIcon size="18px" />
                </IconContainer>
              </ToolTip>
            )}
            {detailType === BaseWorkTaskTypeEnum.SubWorkTask
              ? task?.taskType?.id === parseInt(window._env_.LAUS_TASK_ID)
                ? `LAUS-opgave for: `
                : `Underopgave for: `
              : ''}
            {task?.taskType?.name} {`(${task?.id})`} - {task?.assetId}
            {(task?.status === WorkTaskStatus.Created ||
              task?.status === WorkTaskStatus.ReadyForPlanning ||
              task?.status === WorkTaskStatus.Pause) &&
              !editable &&
              activePanelId === '1' &&
              detailType !== BaseWorkTaskTypeEnum.SubWorkTask && (
                <IconContainer clickable onClick={() => setEditable(true)}>
                  <EditIcon size="24px" padding="0px 0px 0px 5px" />
                </IconContainer>
              )}
          </Typography>
          {task.status && (
            <StatusTag workTaskStatus={task?.status}>{getEnumDisplayValue(WorkTaskStatus[task?.status])}</StatusTag>
          )}
        </StyledTabHeader>
      );
    }
    return <></>;
  };

  const handleDeleteTasks = useCallback(
    async (worktaskHasDifferentDepartment) => {
      if (!id || !task?.status) return;

      if (isInPlanningLoop([task.status])) {
        NotificationService.error(`Kunne ikke annullere opgave(r). En eller flere opgaver er i planning loopet.`);
        return;
      }

      const dialogBody: DialogBody = {
        headerText: `Er du sikker på at du vil annullere opgaven?`,
        bodyText:
          'BEMÆRK: Denne opgave er oprettet i SMILE systemet, og bør kun annulleres direkte i Work efter aftale med opgaveopretteren',
        declineButtonText: 'Fortryd',
        confirmButtonText: 'Ja'
      };

      const confirmation = await getConfirmation(dialogBody);

      if (confirmation === 'confirm') {
        setIsLoading(true);
        TaskService.cancelTasks([parseInt(id)])
          .then(() => {
            dispatch(setListShouldUpdate(true));
            handleCloseTask();
            NotificationService.success(`Annullerede opgave: ${id}.`);
          })
          .catch((error) => {
            NotificationService.error(`Der opstod en fejl ved annullering af opgaven: ${error}`);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    },
    [id, task?.status, getConfirmation, detailType, dispatch, handleCloseTask]
  );

  const setEnableEmailNotificationHandler = (value: boolean) => {
    if (!task?.id) return;
    setIsEmailNotificationLoading(true);
    TaskService.subscribeToEmailNotifications(parseInt(task?.id), EmailNotificationTypeEnum.StatusChange, value)
      .then(() => {
        setEnableEmailNotification(value);
      })
      .catch((error) => {
        log(error);
        NotificationService.error('Det er i øjeblikket ikke muligt at tilmelde sig notifikationer.');
      })
      .finally(() => {
        setIsEmailNotificationLoading(false);
      });
  };

  const handleDeleteWorkInvoice = useCallback(() => {
    if (!workInvoiceId) return;
    WorkInvoiceService.deleteWorkInvoice(workInvoiceId, parseInt(id ?? ''))
      .then(() => {
        setWorkInvoiceId(undefined);
        tabClickedHandler('1');
      })
      .catch((err) => {
        log(err);
        NotificationService.error('Kunne ikke slette skadevolder/regningsarbejde.');
      });
  }, [id, tabClickedHandler]);

  const renderWorkInvoiceNotification = () => {
    switch (showWorkInvoiceError) {
      case InvoiceError.ALERT:
        return (
          <WarningIcon
            color={activePanelId === '10' ? theme.palette.functions.error.tertiary : theme.palette.functions.error.primary}
            size="24px"
          />
        );
      case InvoiceError.NOTIFICATION:
        return <UnreadDot />;
      default:
        return undefined;
    }
  };

  const addContactPersonCallbackHandler = (newContact: ContactInfo) => {
    setMeterDetails((prev) => {
      return {
        ...prev,
        workOrderInfo: prev?.workOrderInfo && {
          ...prev?.workOrderInfo,
          contacts: prev.workOrderInfo.contacts && [...(prev.workOrderInfo.contacts ?? []), newContact]
        }
      };
    });
  };

  const handleAssociateWorkInvoice = useCallback(() => {
    WorkInvoiceService.associateWorkInvoice(parseInt(id ?? ''))
      .then((res) => {
        tabClickedHandler('10');
        setWorkInvoiceId(res);
      })
      .catch((err) => {
        log(err);
        NotificationService.error(`Kunne ikke tilføje skadevolder/regningsarbejde til opgaven: ${id}`);
      });
  }, [id, tabClickedHandler]);

  const subTaskIds = useMemo(() => {
    return [...(task?.subWorkTasks?.map((obj) => obj.id?.toString()) ?? [])];
  }, [task?.subWorkTasks]);

  return (
    <>
      <TabsVertical
        contentLoading={isLoading}
        activePanelId={activePanelId}
        onClose={!hideCloseButton ? handleCloseTask : undefined}
        handleActivePanelIdChange={(id: string) => tabClickedHandler(id)}
        toolsOpen={toolsOpen}
        setToolsOpen={(open: boolean) => setToolsOpen(open)}
        tools={
          <>
            <TabsVerticalToolsItem
              onClick={() => tabClickedHandler('8')}
              htmlFor="image-upload-camera"
              icon={<CameraIcon size="20px" />}
              text="Tag billede"
            />
            <TabsVerticalToolsItem
              onClick={() => handleCreateSubTask(task?.taskType?.id)}
              disabled={
                task?.status === WorkTaskStatus.Completed ||
                task?.status === WorkTaskStatus.Processed ||
                !officeUserGroupAllowed() ||
                detailType === BaseWorkTaskTypeEnum.SubWorkTask
              }
              icon={<AddIcon size="20px" />}
              text="Tilføj ekstra tekniker"
            />
            <TabsVerticalToolsItem
              onClick={() => tabClickedHandler('02')}
              disabled={!!task?.customerAppointment || detailType === BaseWorkTaskTypeEnum.SubWorkTask}
              icon={<ScheduleIcon size="20px" />}
              text="Opret forslag til tidsaftale"
            />
            <TabsVerticalToolsItem
              onClick={handleAssociateWorkInvoice}
              icon={
                <AddDocumentIcon
                  size="20px"
                  color={workInvoiceId ? `${theme.palette.grey.black20}` : `${theme.palette.grey.black90}`}
                />
              }
              text="Tilknyt skadevolder/regningsarbejde"
              disabled={!!workInvoiceId}
            />
            {officeUserGroupAllowed() && (
              <TabsVerticalToolsItem
                onClick={() => setShowWorkTaskControlBar(!showWorkTaskControlBar)}
                icon={<ConstructionIcon size="20px" />}
                text="Vis opgavehandlinger"
              />
            )}
          </>
        }
        enableEmailNotification={enableEmailNotification}
        setEnableEmailNotification={(value) => setEnableEmailNotificationHandler(value)}
        emailNotificationLoading={isEmailNotificationLoading}
      >
        <MeterTaskDetailsStep
          tabId="1"
          task={task}
          customerAppointment={customerAppointment}
          contentHeaderText={renderTabsHeader()}
          tabHeaderText="Opgavedetaljer"
          editable={editable}
          setEditable={setEditable}
          setWorkTaskCallback={setTask}
          scrollIntoPreviousPosition={(id) => scrollIntoPreviousPositionHandler(id)}
          workInvoice={workInvoiceId}
          workInvoiceProjectNumber={workInvoice?.projectId}
          contactPersons={meterDetails?.workOrderInfo?.contacts}
          addContactCallback={addContactPersonCallbackHandler}
          meterDetails={meterDetails}
        />
        <MeterTaskChecklistStep
          tabId="4"
          contentHeaderText={renderTabsHeader()}
          tabHeaderText="Tjekliste"
          workTaskId={task?.id}
          wtmLink={meterDetails?.wtmLink}
          fetchTaskCallback={fetchTaskCallback}
        />
        {!task?.taskType?.preventOrderItems && (
          <TaskGoodsStep
            tabId="5"
            contentHeaderText={renderTabsHeader()}
            tabHeaderText="Varer"
            taskId={task?.id ?? ''}
            projectNumber={task?.projectNumber ?? ''}
            projectLegalEntityId={task?.projectLegalEntityId ?? ''}
            taskLocation={task?.taskLocation}
            taskStatus={task?.status}
            hidden={task?.projectNumber === 'empty'}
          />
        )}
        <TaskTimeRegStep
          tabId="6"
          contentHeaderText={renderTabsHeader()}
          tabHeaderText="Timer"
          idForFetchingProject={task?.projectNumber ?? ''}
          idForRegistration={parseInt(id ?? '')}
          hidden={task?.projectNumber === 'empty'}
        />
        <TaskMessagesStep
          tabId="7"
          taskId={task?.id}
          subTaskId={task?.subTaskId}
          subTaskIds={subTaskIds}
          contentHeaderText={renderTabsHeader()}
          tabHeaderText={`Beskeder ${task?.commentsCount && task?.commentsCount > 0 ? '(' + task?.commentsCount + ')' : ''}`}
          comment={comment}
          setComment={(c) => setComment(c)}
        />
        <TaskFilesStep
          tabId="8"
          taskId={task?.id ?? ''}
          contentHeaderText={renderTabsHeader()}
          tabHeaderText={`Filer ${numberOfFiles && numberOfFiles > 0 ? '(' + numberOfFiles + ')' : ''}`}
          onFileRemoved={handleNumberOfFilesChanged}
          onFileUploaded={handleNumberOfFilesChanged}
        />
        {workInvoiceId && (
          <WorkInvoiceStep
            tabId="10"
            tabHeaderText="Skadevolder/regningsarbejde"
            contentHeaderText={renderTabsHeader()}
            taskStatus={task?.status}
            workInvoiceId={workInvoiceId}
            handleDeleteWorkInvoice={handleDeleteWorkInvoice}
            notification={renderWorkInvoiceNotification()}
            updateNotification={fetchWorkInvoiceNotification}
            isMeterTask
          />
        )}
      </TabsVertical>
      {showWorkTaskControlBar && (
        <WorkTaskControlBar
          selectedWorkTasks={[
            {
              workTaskId: id ?? '',
              hasSubTasks: task?.hasSubTasks ?? false,
              status: task?.status && getEnumDisplayValue(task.status),
              department: task?.assignedToDepartment?.name ?? '-'
            }
          ]}
          updateTasks={fetchTask}
          handleClose={() => setShowWorkTaskControlBar(false)}
          setLoading={(isLoading: boolean) => setIsLoading(isLoading)}
          showBookButton={task?.status === WorkTaskStatus.Created || task?.status === WorkTaskStatus.Pause}
          handleDeleteTasks={handleDeleteTasks}
        />
      )}
    </>
  );
};

const StyledTabHeader = styled.div`
  display: block;
`;

const IconContainer = styled.div<{ clickable?: boolean }>`
  display: inline;
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};
  margin-right: ${(props) => props.theme.spacing(1)};
`;

export default MeterTaskDetailsViewContent;
